import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ShieldCheck, ExternalLink, RefreshCw } from 'lucide-react';
import { apiGet, apiPost } from '../../lib/api.js';
import { Card, StatusBadge, LoadingPanel } from './_shared.jsx';
import { useA2pBypass } from '../../lib/a2pBypassFlag.js';
import A2PBypassBanner from '../../components/A2PBypassBanner.jsx';

export default function A2PTab() {
  const { hideUI: hideA2pUI } = useA2pBypass();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  function load() {
    apiGet('/settings/a2p-status').then((r) => {
      if (r?.ok) setStatus(r.status);
    }).catch((e) => toast.error(e.message || 'Failed to load A2P status'));
  }
  useEffect(load, []);

  async function syncStatus() {
    setBusy(true);
    try {
      // Existing endpoint that pulls fresh status from Twilio.
      await apiPost('/a2p/sync-status');
      toast.success('Status refreshed from Twilio');
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message || 'Sync failed');
    } finally {
      setBusy(false);
    }
  }

  if (hideA2pUI) return <A2PBypassBanner />;

  if (!status) return <LoadingPanel label="Loading A2P compliance status…" />;

  const rows = [
    { label: 'TrustProduct (Customer Profile)', status: status.trustProduct, hint: 'Required before brand submission. Reviewed by Twilio operations.' },
    { label: 'Brand', status: status.brand, hint: status.brandFailureReason || `Tier: ${status.brandTier || 'LOW_VOLUME_STANDARD'} · Trust score: ${status.trustScore ?? '—'}` },
    { label: 'Campaign', status: status.campaign, hint: status.campaignFailureReason || 'Use case: CUSTOMER_CARE / MIXED' },
  ];

  return (
    <div className="space-y-4">
      <Card
        title="A2P 10DLC Compliance"
        description="SMS-to-mobile carriers require A2P registration. CIVIX submits to Twilio TrustHub on your behalf."
        actions={
          <div className="flex items-center gap-2">
            <button onClick={syncStatus} disabled={busy} className="btn-ghost text-sm inline-flex items-center gap-2 disabled:opacity-40">
              <RefreshCw className={`w-4 h-4 ${busy ? 'animate-spin' : ''}`} /> Refresh
            </button>
            <Link to="/business-profile" className="btn-primary text-sm inline-flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" /> Open Business Profile
            </Link>
          </div>
        }
      >
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.label} className="p-3 rounded-lg bg-civix-panelLight border border-civix-border/60">
              <div className="flex items-center justify-between gap-3">
                <div className="font-medium text-sm text-white">{r.label}</div>
                <StatusBadge status={r.status} />
              </div>
              {r.hint && <div className="text-xs text-white/50 mt-1">{r.hint}</div>}
            </div>
          ))}
        </div>

        {status.submittedAt && (
          <div className="text-xs text-white/40 pt-2 border-t border-civix-border/40">
            Submitted {new Date(status.submittedAt).toLocaleString()}
            {status.verifiedAt && <> · Verified {new Date(status.verifiedAt).toLocaleString()}</>}
          </div>
        )}

        <div className="text-xs text-white/45 pt-3 border-t border-civix-border/40">
          Submission, brand resubmission, and campaign creation forms live on the Business
          Profile page. <Link to="/a2p" className="text-civix-purpleSoft hover:underline inline-flex items-center gap-1">
            A2P Wizard <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
      </Card>
    </div>
  );
}
